import cseit from '@/api/cseit'
import person from '@/api/person'
import { cseit as CseitModel } from '@/api/models'
import { mergeUserContext } from '@/store/helpers'

// Someday we will make this an API call vs. statically set here
// Either way our form will be "config" driven which makes altering it much easier
const initialFields = [
  {
    name: 'initial_answer',
    value: 'Yes',
    label: 'Yes, I am certain the youth is exploited'
  },
  {
    name: 'initial_answer',
    value: 'I believe',
    label: 'I believe the youth is being, or has been, commercially sexually exploited but I do not know for sure'
  },
  {
    name: 'initial_answer',
    value: 'I do not believe',
    label: 'No, I have insufficient information to believe the youth is currently exploited, but believe the youth is at high risk for exploitation'
  },
  {
    name: 'initial_answer',
    value: 'No',
    label: 'No, based on information available at the time of screening, the youth is not exploited or at high risk right now'
  },
  {
    name: 'initial_answer',
    value: 'Unknown',
    label: 'Unknown'
  }
]

const canDurationDefaults = [
  { value: 'last 3 mos', text: 'Last 3 Months' },
  { value: 'past year', text: 'Past Year' },
  { value: 'intermittent for 2+ years', text: 'Intermittent for 2+ Years' },
  { value: 'ongoing for 2+ years', text: 'Ongoing for 2+ Years' },
  { value: 'unknown', text: 'Unknown' }
]

const canOnsetDefaults = [
  { value: 'after age 16', text: 'After Age 16' },
  { value: 'age 14-16', text: 'Age 14-16' },
  { value: 'age 12-14', text: 'Age 12-14' },
  { value: 'prior to age 12', text: 'Prior to Age 12' },
  { value: 'unknown', text: 'Unknown' }
]

const canPerceptionDefaults = [
  { value: 'youth is fully aware', text: 'Youth is Fully Aware' },
  { value: 'youth is partially aware', text: 'Youth is Partially Aware' },
  { value: 'youth is unaware', text: 'Youth is Unaware' },
  { value: 'youth actively minimizes', text: 'Youth Actively Minimizes' },
  { value: 'unknown', text: 'Unknown' }
]

// initial state
const state = {
  initialFields: initialFields,
  canDurationDefaults: canDurationDefaults,
  canOnsetDefaults: canOnsetDefaults,
  canPerceptionDefaults: canPerceptionDefaults,
  viewCseit: Object.assign({}, CseitModel),
  createCseit: Object.assign({}, CseitModel),
  createCseitError: '',
  cseitPerson: _.clone(CseitModel),
  personCseits: [],
  cseitQuestions: [],
  personCseitsResponse: false,
  cseitsQuestionsResponse: false,
  recentlyCreatedCseit: false
}

// getters
const getters = {
  getInitialFields: state => state.initialFields,
  getCanDurationDefaults: state => state.canDurationDefaults,
  getCanOnsetDefaults: state => state.canOnsetDefaults,
  getCanPerceptionDefaults: state => state.canPerceptionDefaults,
  getQuestionnaireFields: state => state.questionnaireFields,
  getViewCseit: state => state.viewCseit,
  getCreateCseit: state => state.createCseit,
  getCseitPerson: state => state.cseitPerson,
  getRecentlyCreatedCseit: state => state.recentlyCreatedCseit,
  // Used on Person screen
  getCseitsByPerson: state => state.personCseits,
  getPersonCseitsResponse: state => state.personCseitsResponse,

  getCseitQuestions: state => state.cseitQuestions,
  getCseitQuestionsResponse: state => state.cseitsQuestionsResponse,

  getCreateCseitError: (state) => state.createCseitError
}

/*
 * Vuex actions
 * https://vuex.vuejs.org/guide/actions.html
 */
const actions = {
  // Create
  async createCseit ({commit, state, rootState}, payload) {
    payload = mergeUserContext(payload, rootState)

    var response = await cseit.createCseit(payload)

    if (response.ok) {
      commit('setRecentlyCreatedCseit', response.body)
      commit('setCreateCseit', Object.assign({}, CseitModel))
    } else {
      commit('SET_CREATE_CSEIT_ERROR', response.body)
    }
  },

  // Single View - Edit
  async getCseitPerson ({ commit, state, rootState }, payload) {
    payload = mergeUserContext(payload, rootState)

    payload._id = payload.id
    delete payload.id
    var response = await person.getPerson(payload)

    commit('setCseitPerson', response.body)
  },

  async getCseitsByPerson ({ commit, state, rootState }, payload) {
    payload = mergeUserContext(payload, rootState)

    payload = payload || {}
    var response = await cseit.getCseit(payload)
    response.body.sort(function (a, b) {
      return new Date(b.createdAt) - new Date(a.createdAt)
    })
    response = response.body

    if (_.isArray(response)) {
      commit('setPersonCseits', response)
    } else {
      // TO DO - Set an error
      commit('SET_PERSON_CSEITS_RESPONSE', response)
    }
  },

  async getCseitQuestions ({ commit, state, rootState }, payload) {
    payload = mergeUserContext(payload, rootState)

    payload = payload || {}
    var response = await cseit.getCseitQuestion(payload)
    response = response.body.data

    if (_.isArray(response)) {
      commit('setCseitQuestion', response)
    } else {
      // TO DO - Set an error
      commit('SET_CSEITS_QUESTION_RESPONSE', response)
    }
  },

  // Update/Edit
  async updateCseit ({ commit, state, rootState }, payload) {
    delete payload.sharingGroups
    delete payload.organizationCreator
    delete payload.organizationOwner
    delete payload.person

    payload = mergeUserContext(payload, rootState)

    const user = _.clone(this._vm.$auth.user())

    payload.updatedBy = user.id

    var response = await cseit.updateCseit(payload)
    response = response.body

    // Only show modal if everything went okay
    if (response) {
      commit('setRecentlyCreatedCseit', Object.assign({}, CseitModel))
    }
  },

  // Single View
  async getCseit ({ commit, state, rootState }, payload) {
    payload = mergeUserContext(payload, rootState)

    commit('setCseit', {})

    var response = await cseit.getCseit(payload)
    response = response.body

    if (_.isArray(response)) {
      response = response.pop()
    }

    commit('setCseit', response)
  }
}

/*
 * Vuex mutations
 * https://vuex.vuejs.org/guide/mutations.html
 */
const mutations = {
  setCreateCseit (state, record) {
    state.createCseit = record
  },
  setCseit (state, record) {
    if (_.isEmpty(record)) {
      state.viewCseit = Object.assign({}, CseitModel)
    } else {
      state.viewCseit = record
    }
  },
  setPersonCseits (state, records) {
    if (!_.isEmpty(records)) {
      state.personCseits = records
    } else {
      state.personCseits = []
    }
  },
  setRecentlyCreatedCseit (state, record) {
    state.recentlyCreatedCseit = record
  },
  setCseitQuestion (state, records) {
    if (!_.isEmpty(records)) {
      state.cseitQuestions = records
    } else {
      state.cseitQuestions = []
    }
  },
  setCseitPerson (state, record) {
    if (_.isEmpty(record)) {
      state.cseitPerson = _.clone(CseitModel)
    } else {
      state.cseitPerson = record
    }
  },
  SET_PERSON_CSEITS_RESPONSE (state, response) {
    state.personCseitsResponse = response
  },
  SET_CSEITS_QUESTION_RESPONSE (state, response) {
    state.cseitsQuestions = response
  },

  SET_CREATE_CSEIT_ERROR (state, payload) {
    state.createCseitError = payload
  }
}

export default {
  state,
  getters,
  actions,
  mutations
}



// WEBPACK FOOTER //
// ./src/store/cseit.js